import React, { useEffect, useRef, useState } from 'react'
import { IoMdPause, IoMdPlay } from 'react-icons/io'
import { FiFile } from 'react-icons/fi'
import { formatDuration, waveBars } from '../utils/chatMedia'

export const ChatVoiceBubble = ({ src, mine }) => {
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [current, setCurrent] = useState(0)
  const bars = waveBars(src)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return undefined
    const onTime = () => {
      const length = audio.duration || 0
      setDuration(length)
      setCurrent(audio.currentTime || 0)
      setProgress(length ? audio.currentTime / length : 0)
    }
    const onEnded = () => {
      setPlaying(false)
      setProgress(0)
    }
    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onTime)
    audio.addEventListener('ended', onEnded)
    return () => {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onTime)
      audio.removeEventListener('ended', onEnded)
    }
  }, [src])

  const toggle = () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
  }

  const seek = (event) => {
    const audio = audioRef.current
    if (!audio || !audio.duration) return
    const box = event.currentTarget.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width))
    audio.currentTime = ratio * audio.duration
  }

  const played = Math.floor(bars.length * progress)

  return (
    <div className={`chatVoice${mine ? ' isMine' : ''}`}>
      {/\.webm(\?|$)/i.test(src)
        ? <video ref={audioRef} src={src} preload="metadata" playsInline className="chatVoicePlayer" />
        : <audio ref={audioRef} src={src} preload="metadata" />}
      <button type="button" className="chatVoicePlay" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? <IoMdPause /> : <IoMdPlay />}
      </button>
      <button type="button" className="chatVoiceWave" onClick={seek} aria-label="Seek">
        {bars.map((height, index) => (
          <span
            key={index}
            style={{ height: `${height}%` }}
            className={index <= played ? 'isOn' : ''}
          />
        ))}
      </button>
      <span className="chatVoiceTime">{formatDuration(playing || current ? current : duration)}</span>
    </div>
  )
}

const RING_R = 46
const RING_C = 2 * Math.PI * RING_R

export const ChatVideoNote = ({ src }) => {
  const videoRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return undefined
    const onTime = () => {
      const length = video.duration && Number.isFinite(video.duration) ? video.duration : 0
      setDuration(length)
      setCurrent(video.currentTime || 0)
      setProgress(length ? Math.min(1, video.currentTime / length) : 0)
    }
    const onEnded = () => {
      setPlaying(false)
      setProgress(0)
      setCurrent(0)
    }
    video.addEventListener('timeupdate', onTime)
    video.addEventListener('loadedmetadata', onTime)
    video.addEventListener('ended', onEnded)
    return () => {
      video.removeEventListener('timeupdate', onTime)
      video.removeEventListener('loadedmetadata', onTime)
      video.removeEventListener('ended', onEnded)
    }
  }, [src])

  const seekFromPoint = (clientX, clientY, ring) => {
    const video = videoRef.current
    if (!video || !video.duration || !Number.isFinite(video.duration) || !ring) return
    const box = ring.getBoundingClientRect()
    const angle = Math.atan2(clientY - (box.top + box.height / 2), clientX - (box.left + box.width / 2))
    const ratio = ((angle * 180 / Math.PI) + 90 + 360) % 360 / 360
    video.currentTime = ratio * video.duration
    setProgress(ratio)
    setCurrent(video.currentTime)
  }

  const startSeek = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const ring = event.currentTarget.ownerSVGElement || event.currentTarget
    const point = event.touches && event.touches[0] ? event.touches[0] : event
    seekFromPoint(point.clientX, point.clientY, ring)
    const move = (next) => {
      const p = next.touches && next.touches[0] ? next.touches[0] : next
      seekFromPoint(p.clientX, p.clientY, ring)
    }
    const stop = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('mouseup', stop)
      window.removeEventListener('touchend', stop)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('touchmove', move, { passive: false })
    window.addEventListener('mouseup', stop)
    window.addEventListener('touchend', stop)
  }

  const toggle = () => {
    const video = videoRef.current
    if (!video) return
    if (playing) {
      video.pause()
      setPlaying(false)
      return
    }
    video.play().then(() => setPlaying(true)).catch(() => {
      video.muted = true
      video.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
    })
  }

  return (
    <div className="chatVideoNote">
      <svg className="chatVideoRing" viewBox="0 0 100 100" aria-label="Video timeline">
        <circle className="chatVideoRingTrack" cx="50" cy="50" r={RING_R} />
        <circle
          className="chatVideoRingHit"
          cx="50"
          cy="50"
          r={RING_R}
          onMouseDown={startSeek}
          onTouchStart={startSeek}
        />
        <circle
          className="chatVideoRingProgress"
          cx="50"
          cy="50"
          r={RING_R}
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - progress)}
        />
      </svg>
      <div className="chatVideoClip">
        <video
          ref={videoRef}
          src={src}
          playsInline
          onEnded={() => { setPlaying(false); setProgress(0); setCurrent(0); }}
          onClick={toggle}
        />
        {!playing ? (
          <button type="button" className="chatVideoPlay" onClick={toggle} aria-label="Play video">
            <IoMdPlay />
          </button>
        ) : null}
        <span className="chatVideoTime">{formatDuration(playing || current ? current : duration)}</span>
      </div>
    </div>
  )
}

export const ChatFileCard = ({ src, label }) => (
  <a className="chatFileCard" href={src} download={label} target="_blank" rel="noreferrer">
    <span className="chatFileIcon"><FiFile /></span>
    <span className="chatFileMeta">
      <span className="chatFileName">{label || 'File'}</span>
      <span className="chatFileHint">Tap to open</span>
    </span>
  </a>
)
